import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Mic,
  MicOff,
  Moon,
  Loader2,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * End-of-Day Conversational Checkout
 * User speaks or types what they did today.
 * Niko parses it, saves the checkout, and creates tomorrow's carry-forward tasks.
 * Nothing is stored as an AI training log — only the structured result lives in your MongoDB.
 */
export default function EndOfDayCheckout({ open, onClose, onComplete }) {
  const [transcript, setTranscript] = useState("");
  const [listening, setListening] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [openContext, setOpenContext] = useState(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setTranscript("");
      setResult(null);
      setError("");
      setSubmitting(false);
      setOpenContext(null);
      stopListening();
      return;
    }
    // Cross-surface memory: pull open items so the user can see what's still
    // hanging before they recap — and Niko can reference them in reflection.
    axios.get(`${API}/checkout-open-items`, { withCredentials: true })
      .then((r) => setOpenContext(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stopListening = () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) { /* noop */ }
    }
    setListening(false);
  };

  const startListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setError("Voice dictation isn't supported in this browser. Please type your recap.");
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";

    let finalSoFar = transcript ? transcript + " " : "";

    rec.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalSoFar += chunk + " ";
        } else {
          interim += chunk;
        }
      }
      setTranscript((finalSoFar + interim).replace(/\s+/g, " "));
    };
    rec.onerror = (e) => {
      setError(`Voice error: ${e.error || "unknown"}`);
      setListening(false);
    };
    rec.onend = () => setListening(false);

    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  };

  const submit = async () => {
    setError("");
    setSubmitting(true);
    stopListening();
    try {
      const today = new Date().toISOString().split("T")[0];
      const res = await axios.post(
        `${API}/checkout/converse`,
        { date: today, transcript: transcript.trim() },
        { withCredentials: true }
      );
      setResult(res.data);
      if (onComplete) onComplete(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save your checkout. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="eod-checkout-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-zinc-950 text-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-auto border border-purple-500/30 shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-900/80 to-indigo-900/80 p-5 flex items-center justify-between border-b border-purple-500/30 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Moon className="w-5 h-5 text-purple-300" />
            </div>
            <div>
              <div className="font-bold text-lg">End of Day Checkout</div>
              <div className="text-xs text-purple-200/80">Talk to Niko about your day — I'll log it and set up tomorrow.</div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="eod-close-btn"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {!result && (
            <>
              <div className="text-sm text-zinc-300 leading-relaxed">
                What did you get done today? What's still hanging? Any wins or blockers?
                Speak freely — Niko will pull out accomplishments, carry-forwards, energy, and a win.
              </div>

              {/* Cross-surface context: what's still open coming into this checkout */}
              {openContext && (openContext.counts?.open_sprints + openContext.counts?.unreviewed_incidents + openContext.counts?.stale_tasks) > 0 && (
                <div
                  className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/30 space-y-2"
                  data-testid="eod-open-context"
                >
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-300 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" />
                    Still open coming into this recap
                  </div>
                  <div className="grid md:grid-cols-3 gap-2 text-xs">
                    {openContext.open_sprints?.length > 0 && (
                      <div data-testid="eod-context-sprints">
                        <div className="text-purple-300/80 font-semibold mb-1">
                          Open sprints ({openContext.open_sprints.length})
                        </div>
                        <ul className="text-zinc-400 space-y-0.5">
                          {openContext.open_sprints.slice(0, 3).map((s) => (
                            <li key={s.item_id} className="truncate">
                              · {s.area_name ? `${s.area_name}: ` : ""}{s.task_text}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {openContext.unreviewed_incidents?.length > 0 && (
                      <div data-testid="eod-context-incidents">
                        <div className="text-red-300/80 font-semibold mb-1">
                          Unreviewed incidents ({openContext.unreviewed_incidents.length})
                        </div>
                        <ul className="text-zinc-400 space-y-0.5">
                          {openContext.unreviewed_incidents.slice(0, 3).map((i) => (
                            <li key={i.incident_id} className="truncate">
                              · {i.unit}: {i.title}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {openContext.stale_tasks?.length > 0 && (
                      <div data-testid="eod-context-stale">
                        <div className="text-amber-300/80 font-semibold mb-1">
                          Zombie tasks ({openContext.stale_tasks.length})
                        </div>
                        <ul className="text-zinc-400 space-y-0.5">
                          {openContext.stale_tasks.slice(0, 3).map((t) => (
                            <li key={t.task_id} className="truncate">
                              · {t.description} <span className="text-amber-400">({t.days_old}d)</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] text-zinc-500 italic">
                    Not required to mention — just a quiet reminder. Niko will reference anything you bring up.
                  </p>
                </div>
              )}

              <div className="relative">
                <Textarea
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  placeholder="Example: Rounded on 300 and 400, finished the SOP review, and caught a near miss at huddle. Didn't finish PI council prep — need to tackle that tomorrow. Decent energy, maybe a 4."
                  className="min-h-[180px] bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 resize-y focus:border-purple-500"
                  data-testid="eod-transcript-input"
                  disabled={submitting}
                />
                {listening && (
                  <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/20 border border-red-500/40 text-xs text-red-300 font-semibold">
                    <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
                    Listening
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={listening ? stopListening : startListening}
                  variant={listening ? "destructive" : "outline"}
                  className={listening
                    ? "bg-red-500/20 border-red-500 text-red-300 hover:bg-red-500/30"
                    : "bg-zinc-900 border-zinc-700 text-white hover:bg-zinc-800"
                  }
                  data-testid="eod-mic-btn"
                  disabled={submitting}
                >
                  {listening ? <MicOff className="w-4 h-4 mr-2" /> : <Mic className="w-4 h-4 mr-2" />}
                  {listening ? "Stop" : "Dictate"}
                </Button>

                <Button
                  onClick={submit}
                  disabled={!transcript.trim() || submitting}
                  className="bg-purple-600 hover:bg-purple-500 text-white ml-auto"
                  data-testid="eod-submit-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" />Close out my day</>
                  )}
                </Button>
              </div>

              {error && (
                <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="eod-error">
                  {error}
                </div>
              )}

              <div className="flex items-start gap-2 pt-3 border-t border-zinc-800 text-xs text-zinc-400">
                <ShieldCheck className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span>
                  Private by design. Your recap is sent to the LLM once to structure it, then stored only in your MongoDB.
                  Niko does not retain transcripts for training.
                </span>
              </div>
            </>
          )}

          {result && (
            <div className="space-y-4" data-testid="eod-result">
              <div className="p-4 rounded-xl bg-gradient-to-br from-purple-900/30 to-indigo-900/20 border border-purple-500/30">
                <div className="flex items-center gap-2 text-purple-200 text-xs font-semibold uppercase tracking-wide mb-2">
                  <Sparkles className="w-3.5 h-3.5" /> Niko's reflection
                </div>
                <div className="text-sm text-white leading-relaxed">{result.reflection}</div>
              </div>

              {result.accomplishments?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-emerald-300 mb-2">
                    ✓ Accomplishments ({result.accomplishments.length})
                  </div>
                  <ul className="space-y-1.5">
                    {result.accomplishments.map((a, i) => (
                      <li key={`acc-${i}`} className="flex items-start gap-2 text-sm text-zinc-200">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.checkout?.wins && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <div className="text-xs font-semibold text-emerald-300 mb-1">🏆 Win</div>
                  <div className="text-sm text-white">{result.checkout.wins}</div>
                </div>
              )}

              {result.checkout?.blockers && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <div className="text-xs font-semibold text-amber-300 mb-1">⚠ Blocker</div>
                  <div className="text-sm text-white">{result.checkout.blockers}</div>
                </div>
              )}

              {result.created_tasks?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-blue-300 mb-2">
                    → Carried to tomorrow ({result.created_tasks.length})
                  </div>
                  <ul className="space-y-1.5">
                    {result.created_tasks.map((t) => (
                      <li key={t.task_id} className="flex items-start gap-2 text-sm text-zinc-200">
                        <ArrowRight className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                        <span>{t.description}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-center gap-4 pt-2 text-sm">
                {result.checkout?.energy_level != null && (
                  <div className="flex items-center gap-2 text-zinc-300">
                    <span className="text-xs uppercase text-zinc-500">Energy</span>
                    <span className="font-semibold text-white">{result.checkout.energy_level}/5</span>
                  </div>
                )}
                {result.mood && (
                  <div className="flex items-center gap-2 text-zinc-300">
                    <span className="text-xs uppercase text-zinc-500">Mood</span>
                    <span className="font-semibold text-white capitalize">{result.mood}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t border-zinc-800">
                <Button
                  onClick={() => { setResult(null); setTranscript(""); }}
                  variant="outline"
                  className="bg-zinc-900 border-zinc-700 text-white hover:bg-zinc-800"
                  data-testid="eod-add-more-btn"
                >
                  Add more
                </Button>
                <Button
                  onClick={onClose}
                  className="bg-purple-600 hover:bg-purple-500 text-white ml-auto"
                  data-testid="eod-done-btn"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
